import type { CustomUserClaims } from "@okta/okta-auth-js";
import { useAuth } from "@okta/okta-vue";

export const KUWAIT_OIL_COMPANY_ID = "322083";
export const HESS_CUSTOMER_ID = "367781"; // Added constant for Hess customer ID

export const ECOPETROL_IDS = [
  "379617",
  "364925",
  "363960",
  "362761",
  "351231",
  "348798",
  "343517",
  "317062"
];

export type Roles =
  | "customer"
  | "spyglass_admin"
  | "spyglass_superuser"
  | "registered_user"
  | "ecopetrol_customer"
  | "koc_customer";

export const SpyglassRoles: Record<Roles, Roles> = {
  customer: "customer",
  spyglass_admin: "spyglass_admin",
  spyglass_superuser: "spyglass_superuser",
  registered_user: "registered_user",
  ecopetrol_customer: "ecopetrol_customer",
  koc_customer: "koc_customer"
};

export interface SpyglassUser {
  customers?: Array<string>;
  display_name?: string;
  email?: string;
  first_name?: string;
  spyglass_groups?: Array<string>;
  id?: string;
  identifier?: string;
  role?: string;
  display_role?: string;
  is_customer?: boolean;
  is_admin?: boolean;
  is_superuser?: boolean;
  has_elevated_privileges?: boolean;
  is_registered_user?: boolean;
  is_koc_customer?: boolean;
  is_hess_customer?: boolean;
  is_ecopetrol_customer?: boolean;
  "x-hasura-default-role"?: string;
  "x-hasura-user-id"?: string;
  eulaAccepted?: boolean;
  is_s4_user?: boolean;
}

export default (): CustomUserClaims => {
  const authClient = useAuth();
  const authState = authClient?.authStateManager.getAuthState();

  if (!authState) {
    return {};
  }

  const id_claims = authState.idToken?.claims as CustomUserClaims;
  const access_claims = authState.accessToken?.claims as CustomUserClaims;

  if (!access_claims?.["x-hasura-default-role"]) {
    return {};
  }

  const userRole = access_claims["x-hasura-default-role"] as Roles;

  const is_superuser = userRole === SpyglassRoles.spyglass_superuser;
  const is_admin = userRole === SpyglassRoles.spyglass_admin;
  const is_customer =
    userRole === SpyglassRoles.customer ||
    userRole === SpyglassRoles.ecopetrol_customer ||
    userRole === SpyglassRoles.koc_customer;
  const is_registered_user = !is_admin && !is_superuser && !is_customer;
  const has_elevated_privileges = is_admin || is_superuser;

  let display_role = "System User";
  if (is_superuser) {
    display_role = "Superuser";
  } else if (is_admin) {
    display_role = "Admin";
  } else if (userRole === SpyglassRoles.koc_customer) {
    display_role = "KOC Customer";
  } else if (is_customer) {
    display_role = "Customer";
  } else if (userRole === SpyglassRoles.ecopetrol_customer) {
    display_role = "Ecopetrol Customer";
  }

  const is_hess_customer =
    is_customer &&
    Array.isArray(id_claims?.customers) &&
    id_claims.customers.includes(HESS_CUSTOMER_ID);

  const getClaimGroups = (claims: CustomUserClaims | undefined) => [
    ...(Array.isArray(claims?.spyglass_groups) ? claims.spyglass_groups : []),
    ...(Array.isArray(claims?.reaver_groups) ? claims.reaver_groups : []),
    ...(Array.isArray(claims?.groups) ? claims.groups : [])
  ];

  const hasGroupClaims = (claims: CustomUserClaims | undefined) =>
    Array.isArray(claims?.spyglass_groups) ||
    Array.isArray(claims?.reaver_groups) ||
    Array.isArray(claims?.groups);

  // S4 group claims may be present on either token depending on Okta app config.
  const claimGroups = [...getClaimGroups(id_claims), ...getClaimGroups(access_claims)];
  const is_s4_user = hasGroupClaims(id_claims) || hasGroupClaims(access_claims)
    ? claimGroups.includes("s4_user")
    : undefined;

  return {
    ...id_claims,
    id: id_claims?.sub || "",
    role: userRole,
    customers: id_claims?.customers,
    display_role,
    hal_identifier: id_claims?.identifier,
    is_customer,
    is_admin,
    is_superuser,
    has_elevated_privileges,
    is_registered_user,
    is_koc_customer: userRole === SpyglassRoles.koc_customer,
    is_ecopetrol_customer: userRole === SpyglassRoles.ecopetrol_customer,
    eulaAccepted: false,
    is_hess_customer,
    ...(is_s4_user === undefined ? {} : { is_s4_user })
  };
};
